import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import { Alert, Button, Input, Label, List, ListItem, Well } from '@feathr/components';
import { objectId } from '@feathr/hooks';

import type { IAdGroupKeyword } from '../AdStep.types';
import KeywordChip from './KeywordChip';
import { getKeywordMessages } from './KeywordFields.utils';

import * as styles from './AdGroupEditor.css';

interface INegativeKeywordFieldsProps {
  negativeKeywords: IAdGroupKeyword[];
  /** Messages for every keyword that has any, keyed by keyword key. Shared with the
   * positive editor, because the two are one list on the model. */
  keywordErrors: Record<string, string[]>;
  onChange: (negativeKeywords: IAdGroupKeyword[]) => void;
  disabled?: boolean;
}

/**
 * Terms that suppress the ad. Same chip-and-add interaction as the positive
 * keywords, minus the match type — negative keywords don't carry one, and the
 * single-word policy check doesn't apply to them.
 *
 * @param props - {@link INegativeKeywordFieldsProps}.
 */
function NegativeKeywordFields({
  negativeKeywords,
  keywordErrors,
  onChange,
  disabled = false,
}: Readonly<INegativeKeywordFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [draft, setDraft] = useState('');
  const messages = getKeywordMessages(negativeKeywords, keywordErrors);

  function handleAdd(): void {
    const text = draft.trim();
    if (!text) {
      return;
    }
    onChange([
      ...negativeKeywords,
      { key: objectId(), text, matchType: EGoogleAdsKeywordMatchType.BROAD },
    ]);
    setDraft('');
  }

  function handleRemove(index: number): void {
    onChange(negativeKeywords.filter((_, i) => i !== index));
  }

  function handleDraftChange(value?: string): void {
    setDraft(value ?? '');
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  }

  return (
    <>
      <Label weight={'medium'}>{t('Negative keywords')}</Label>
      <Well layout={'vertical'} theme={'white'} width={'full'}>
        {!!negativeKeywords.length && (
          <Flex gap={theme.spacing.half} wrap={'wrap'}>
            {negativeKeywords.map((keyword, index) => (
              <KeywordChip
                disabled={disabled}
                hasError={!!keywordErrors[keyword.key]?.length}
                index={index}
                key={keyword.key}
                keyword={keyword}
                onRemove={handleRemove}
              />
            ))}
          </Flex>
        )}
        {!!messages.length && (
          <Alert
            className={styles.alertInWell}
            title={t('Fix the keywords marked in red.')}
            type={'danger'}
          >
            <List>
              {messages.map((message) => (
                <ListItem key={message}>{message}</ListItem>
              ))}
            </List>
          </Alert>
        )}
        <Flex align={'flex-end'} className={styles.addRow} wrap={'wrap'}>
          <Input
            disabled={disabled}
            helpText={t("Searches containing these terms won't trigger your ads.")}
            label={t('Add a negative keyword')}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            placeholder={t('Term to exclude')}
            type={'text'}
            value={draft}
            wrapperClassName={styles.grow}
          />
          <Button
            disabled={disabled || !draft.trim()}
            name={'add-negative-keyword'}
            onClick={handleAdd}
            prefix={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('Add negative keyword')}
          </Button>
        </Flex>
      </Well>
    </>
  );
}

export default NegativeKeywordFields;
